import { useEffect, useState, useCallback } from 'react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../store/auth'
import { useToast } from '../ui/Toast'
import { StatusBadge } from '../ui/StatusBadge'
import { DeviceIcon } from '../ui/DeviceIcon'
import { GlowCard } from '../ui/GlowCard'
import { Button } from '../ui/Button'
import type { SiteDeviceWithSpec } from '@shared/types'

const PlusIcon = <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>

const DEVICE_TYPE_OPTIONS = [
  'switch', 'gateway', 'access_point', 'camera', 'nvr', 'access_controller',
  'intercom', 'fire_panel', 'ups', 'patch_panel', 'server', 'workstation',
  'voip_phone', 'doorbell', 'speaker', 'encoder', 'other',
] as const

const STATUS_OPTIONS = ['online', 'offline', 'installed', 'planned', 'decommissioned'] as const

const _CATEGORY_ICONS: Record<string, string> = {
  camera: '◉', nvr: '▣', vms_server: '▣', switch: '⬡', patch_panel: '⬡',
  controller: '◈', reader: '◈', speaker: '♫', ups: '⚡', pdu: '⚡',
  router: '⬡', access_point: '⬡', intercom: '♫', other: '●',
}

const CATEGORY_COLORS: Record<string, string> = {
  camera: 'var(--color-camera)', nvr: 'var(--color-networking)', vms_server: 'var(--color-networking)', switch: 'var(--color-networking)',
  patch_panel: 'var(--color-networking)', controller: 'var(--color-access)', reader: 'var(--color-access)',
  speaker: 'var(--color-software)', ups: 'var(--color-power)', pdu: 'var(--color-power)',
  router: 'var(--color-networking)', access_point: 'var(--color-networking)', intercom: 'var(--color-software)', other: 'var(--color-infra)',
}

interface Props {
  siteId: string
  onOpenDevice: (slug: string) => void
  onOpenNetworkDevice?: (deviceId: string, highlightPort?: number) => void
  initialFilter?: string
}

const NETWORK_DEVICE_TYPES = new Set(['switch'])

interface InfraGroup {
  id: string
  name: string
  type: string
  devices: SiteDeviceWithSpec[]
}

export function SiteDevicesTab({ siteId, onOpenDevice, onOpenNetworkDevice, initialFilter }: Props) {
  const [devices, setDevices] = useState<SiteDeviceWithSpec[]>([])
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()
  const [expandedInfra, setExpandedInfra] = useState<string[]>([])
  const [filter, setFilter] = useState<string>(initialFilter || 'all')
  const [showAddDevice, setShowAddDevice] = useState(false)
  const [addDeviceForm, setAddDeviceForm] = useState({
    label: '', device_type: '' as string, device_model: '', ip_address: '',
    mac_address: '', serial_number: '', status: 'installed' as string, notes: '',
  })
  const [addDeviceSaving, setAddDeviceSaving] = useState(false)

  const loadDevices = useCallback(() => {
    api.get<{ data: SiteDeviceWithSpec[] }>(`/sites/${siteId}/devices?limit=200`)
      .then(r => {
        setDevices(r.data)
        const infraIds = new Set(r.data.map(d => d.infrastructure_id || 'unassigned'))
        setExpandedInfra([...infraIds])
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  useEffect(() => { loadDevices() }, [loadDevices])

  const toggle = (id: string) => {
    setExpandedInfra(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])
  }

  // Category groups for filtering (e.g. clicking "Cameras" shows both camera & surveillance)
  const CATEGORY_GROUPS: Record<string, string[]> = {
    camera: ['camera', 'surveillance'],
    controller: ['controller', 'access_control'],
    switch: ['switch', 'networking'],
    nvr: ['nvr', 'vms_server'],
    ups: ['ups', 'power'],
    server: ['server', 'infrastructure'],
  }

  // Use device_type as fallback when spec_category is missing
  const getCategory = (d: SiteDeviceWithSpec): string => {
    return d.spec_category || d.device_type || 'other'
  }

  const filtered = filter === 'all'
    ? devices
    : devices.filter(d => {
        const cat = getCategory(d)
        const group = CATEGORY_GROUPS[filter]
        return group ? group.includes(cat) : cat === filter
      })

  // Group by infrastructure
  const groups: InfraGroup[] = []
  const infraMap = new Map<string, SiteDeviceWithSpec[]>()
  filtered.forEach(d => {
    const key = d.infrastructure_id || 'unassigned'
    if (!infraMap.has(key)) infraMap.set(key, [])
    infraMap.get(key)!.push(d)
  })

  infraMap.forEach((devs, key) => {
    groups.push({
      id: key,
      name: key === 'unassigned' ? 'Unassigned' : `Infrastructure ${key.slice(0, 8)}`,
      type: key === 'unassigned' ? 'none' : 'mdf',
      devices: devs,
    })
  })

  // Get unique categories for filter (union of spec_category and device_type)
  const categories = [...new Set(devices.map(d => getCategory(d)).filter(c => c && c !== 'other'))]

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton" style={{ height: 60, marginBottom: 12, borderRadius: 'var(--radius-lg)' }} />
        ))}
      </div>
    )
  }

  return (
    <div>
      {/* Sticky tab header */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg-0)', paddingBottom: 12, marginBottom: 12 }}>
        <div className="flex justify-between items-center" style={{ marginBottom: 20 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700 }}>All Devices</h3>
          <div className="flex gap-2">
            <Button variant="secondary" size="sm" onClick={() => exportCsv(siteId, 'cable-schedule', 'Cable Schedule', toast)}>Export Cable Schedule</Button>
            <Button variant="secondary" size="sm" onClick={() => exportCsv(siteId, 'port-map', 'Port Map', toast)}>Export Port Map</Button>
            <Button size="sm" onClick={() => setShowAddDevice(true)} icon={PlusIcon}>Add Device</Button>
          </div>
        </div>

        {/* Category filter */}
        {categories.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <button
              onClick={() => setFilter('all')}
              style={{
                padding: '4px 12px', borderRadius: 'var(--radius-full)', border: 'none',
                fontSize: 11, fontWeight: 600, cursor: 'pointer',
                background: filter === 'all' ? 'var(--primary)' : 'var(--bg-inset)',
                color: filter === 'all' ? '#fff' : 'var(--text-secondary)',
              }}
            >All ({devices.length})</button>
            {categories.map(cat => {
              const count = devices.filter(d => getCategory(d) === cat).length
              const color = CATEGORY_COLORS[cat || 'other'] || 'var(--color-infra)'
              return (
                <button
                  key={cat}
                  onClick={() => setFilter(cat || 'all')}
                  style={{
                    padding: '4px 12px', borderRadius: 'var(--radius-full)', border: 'none',
                    fontSize: 11, fontWeight: 600, cursor: 'pointer',
                    background: filter === cat ? `${color}20` : 'var(--bg-inset)',
                    color: filter === cat ? color : 'var(--text-secondary)',
                  }}
                >{cat} ({count})</button>
              )
            })}
          </div>
        )}
      </div>

      {/* Device groups */}
      {groups.length > 0 ? groups.map((group, gi) => (
        <GlowCard key={group.id} style={{ marginBottom: 16, padding: 0, overflow: 'hidden' }} delay={gi * 60}>
          <div
            onClick={() => toggle(group.id)}
            className="flex items-center justify-between"
            style={{
              padding: '14px 20px', cursor: 'pointer',
              borderBottom: expandedInfra.includes(group.id) ? '1px solid var(--border-light)' : 'none',
            }}
          >
            <div className="flex items-center gap-3">
              <div style={{
                width: 28, height: 28, borderRadius: 8,
                background: 'rgba(5,150,105,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, color: 'var(--success)',
              }}>▣</div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700 }}>{group.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{group.devices.length} devices</div>
              </div>
            </div>
            <span style={{
              fontSize: 18, color: 'var(--text-tertiary)',
              transform: expandedInfra.includes(group.id) ? 'rotate(180deg)' : 'none',
              transition: 'transform 0.15s',
            }}>▾</span>
          </div>

          {expandedInfra.includes(group.id) && group.devices.map(dev => {
            return (
              <div
                key={dev.id}
                onClick={() => {
                  const isNetworkDevice = NETWORK_DEVICE_TYPES.has(dev.device_type || '') ||
                    NETWORK_DEVICE_TYPES.has(dev.spec_category || '')
                  if (isNetworkDevice && onOpenNetworkDevice) {
                    onOpenNetworkDevice(dev.id)
                  } else {
                    onOpenDevice(dev.slug)
                  }
                }}
                className="flex items-center gap-3"
                style={{
                  padding: '11px 20px',
                  borderBottom: '1px solid var(--border-light)',
                  cursor: 'pointer', transition: 'background 0.1s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-surface-hover)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
              >
                <DeviceIcon
                  iconUrl={dev.icon_url}
                  deviceType={dev.device_type}
                  model={dev.device_model}
                  size={32}
                />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="flex items-center gap-1.5">
                    <span style={{ fontSize: 13, fontWeight: 600, fontFamily: 'var(--font-mono)' }}>{dev.label || dev.spec_brand + ' ' + dev.spec_model}</span>
                    {(dev.status === 'online' || (dev.status as string) === 'active') && (
                      <div style={{ width: 6, height: 6, borderRadius: 'var(--radius-full)', background: 'var(--success)' }} />
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)' }}>{dev.spec_brand ? `${dev.spec_brand} ${dev.spec_model || ''}` : dev.device_model || ''}</div>
                </div>
                {dev.ip_address && (
                  <span style={{
                    fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-tertiary)',
                    background: 'var(--bg-inset)', padding: '2px 6px', borderRadius: 4,
                  }}>{dev.ip_address}</span>
                )}
                {dev.status && <StatusBadge status={dev.status} />}
              </div>
            )
          })}
        </GlowCard>
      )) : (
        <GlowCard style={{ textAlign: 'center', padding: '48px 24px' }} delay={0}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📱</div>
          <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>No devices</div>
          <p style={{ fontSize: 13, color: 'var(--text-tertiary)', marginBottom: 16 }}>
            Add devices to this site from the floorplan designer or manually.
          </p>
          <Button size="sm" onClick={() => setShowAddDevice(true)} icon={PlusIcon}>Add Device</Button>
        </GlowCard>
      )}

      {/* Add Device Modal */}
      {showAddDevice && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} onClick={() => setShowAddDevice(false)} />
          <div style={{ position: 'relative', background: 'var(--bg-surface)', borderRadius: 'var(--radius-xl, 12px)', padding: 24, width: 480, maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--shadow-xl)' }}>
            <h3 style={{ fontSize: 16, fontWeight: 700, marginBottom: 16 }}>Add Device</h3>
            <form onSubmit={async (e) => {
              e.preventDefault()
              setAddDeviceSaving(true)
              try {
                await api.post(`/sites/${siteId}/devices`, {
                  label: addDeviceForm.label,
                  device_type: addDeviceForm.device_type || undefined,
                  device_model: addDeviceForm.device_model || undefined,
                  ip_address: addDeviceForm.ip_address || undefined,
                  mac_address: addDeviceForm.mac_address || undefined,
                  serial_number: addDeviceForm.serial_number || undefined,
                  status: addDeviceForm.status,
                  notes: addDeviceForm.notes || undefined,
                  source: 'manual',
                })
                setShowAddDevice(false)
                setAddDeviceForm({ label: '', device_type: '', device_model: '', ip_address: '', mac_address: '', serial_number: '', status: 'installed', notes: '' })
                loadDevices()
              } catch { toast('Failed to add device', 'error') }
              finally { setAddDeviceSaving(false) }
            }}>
              <div style={{ marginBottom: 12 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Name *</label>
                <input className="v3-input" required value={addDeviceForm.label} onChange={e => setAddDeviceForm(f => ({ ...f, label: e.target.value }))} placeholder="e.g. Main Switch" />
              </div>
              <div className="flex gap-3" style={{ marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Device Type *</label>
                  <div className="v3-select-wrap">
                    <select className="v3-select" required value={addDeviceForm.device_type} onChange={e => setAddDeviceForm(f => ({ ...f, device_type: e.target.value }))}>
                      <option value="">Select type...</option>
                      {DEVICE_TYPE_OPTIONS.map(t => (
                        <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                      ))}
                    </select>
                    <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                  </div>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Model</label>
                  <input className="v3-input" value={addDeviceForm.device_model} onChange={e => setAddDeviceForm(f => ({ ...f, device_model: e.target.value }))} placeholder="e.g. USW-Pro-48" />
                </div>
              </div>
              <div className="flex gap-3" style={{ marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>IP Address</label>
                  <input className="v3-input" value={addDeviceForm.ip_address} onChange={e => setAddDeviceForm(f => ({ ...f, ip_address: e.target.value }))} placeholder="e.g. 192.168.1.1" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>MAC Address</label>
                  <input className="v3-input" value={addDeviceForm.mac_address} onChange={e => setAddDeviceForm(f => ({ ...f, mac_address: e.target.value }))} placeholder="e.g. AA:BB:CC:DD:EE:FF" />
                </div>
              </div>
              <div className="flex gap-3" style={{ marginBottom: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Serial Number</label>
                  <input className="v3-input" value={addDeviceForm.serial_number} onChange={e => setAddDeviceForm(f => ({ ...f, serial_number: e.target.value }))} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Status</label>
                  <div className="v3-select-wrap">
                    <select className="v3-select" value={addDeviceForm.status} onChange={e => setAddDeviceForm(f => ({ ...f, status: e.target.value }))}>
                      {STATUS_OPTIONS.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                    <span className="v3-select-chevron"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6,9 12,15 18,9" /></svg></span>
                  </div>
                </div>
              </div>
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, display: 'block', marginBottom: 4 }}>Notes</label>
                <textarea className="v3-textarea" rows={2} value={addDeviceForm.notes} onChange={e => setAddDeviceForm(f => ({ ...f, notes: e.target.value }))} />
              </div>
              <div className="flex gap-2 justify-end">
                <Button type="button" variant="secondary" onClick={() => setShowAddDevice(false)}>Cancel</Button>
                <Button type="submit" loading={addDeviceSaving}>Add Device</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function exportCsv(siteId: string, path: string, label: string, toast: (msg: string, type?: 'success' | 'error' | 'info') => void) {
  const token = useAuthStore.getState().token
  const url = `/api/sites/${siteId}/export/${path}`
  fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
    .then(res => res.blob())
    .then(blob => {
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${path}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    })
    .catch(() => toast(`Failed to download ${label}`, 'error'))
}
